// v2 Settings — adds Living Cast toggle (UI only) + 50-voice pool readout
// alongside the existing handle/bio/overlay/account/danger sections.

import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { api, getMe } from "../../lib/api";
import { VOICE_POOL } from "../../lib/voices";
import "./dashboard.css";

export default function DashboardSettings(): JSX.Element {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: getMe, staleTime: 30_000 });
  const user = me.data?.user;
  const streamer = me.data?.streamer ?? null;

  const [bio, setBio] = useState<string>("");
  const [bioSaving, setBioSaving] = useState(false);
  const [bioSaved, setBioSaved] = useState(false);
  const [bioErr, setBioErr] = useState<string | null>(null);

  const [handle, setHandle] = useState<string>("");
  const [handleSaving, setHandleSaving] = useState(false);
  const [handleSaved, setHandleSaved] = useState(false);
  const [handleErr, setHandleErr] = useState<string | null>(null);

  const [rotating, setRotating] = useState(false);
  const [rotateErr, setRotateErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // v2 toggle — UI-only for now. TODO: wire to a streamer.ai_enabled field
  // and gate the imagen + voice pipeline in functions/api/donations.
  const [aiEnabled, setAiEnabled] = useState(true);

  useEffect(() => {
    if (streamer && bio === "") setBio(streamer.bio ?? "");
  }, [streamer, bio]);
  useEffect(() => {
    if (user && handle === "") setHandle(user.handle ?? "");
  }, [user, handle]);

  if (me.isLoading) return <p style={{ padding: 20 }}>Loading…</p>;
  if (!user) return <Navigate to="/login?returnUrl=/dashboard/settings" replace />;

  const overlayUrl = streamer
    ? `${window.location.origin}/overlay/${streamer.overlay_key}`
    : null;

  async function saveHandle(): Promise<void> {
    setHandleSaving(true);
    setHandleErr(null);
    setHandleSaved(false);
    try {
      const next = handle.trim().toLowerCase();
      if (next === user?.handle) {
        setHandleSaved(true);
        setTimeout(() => setHandleSaved(false), 2000);
        return;
      }
      const res = await fetch("/api/me/profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ handle: next }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      setHandleSaved(true);
      await qc.invalidateQueries({ queryKey: ["me"] });
      setTimeout(() => setHandleSaved(false), 2000);
    } catch (e) {
      setHandleErr((e as Error).message);
    } finally {
      setHandleSaving(false);
    }
  }

  async function saveBio(): Promise<void> {
    setBioSaving(true);
    setBioErr(null);
    setBioSaved(false);
    try {
      await api.patch<{ ok: true }>("/api/me/profile", { bio });
      setBioSaved(true);
      await qc.invalidateQueries({ queryKey: ["me"] });
      setTimeout(() => setBioSaved(false), 2000);
    } catch (e) {
      setBioErr((e as Error).message);
    } finally {
      setBioSaving(false);
    }
  }

  async function rotate(): Promise<void> {
    if (!confirm("Rotate overlay URL? Your current OBS browser source will stop working until you paste the new one.")) return;
    setRotating(true);
    setRotateErr(null);
    try {
      await api.post<{ ok: true; overlay_key: string }>("/api/me/rotate-overlay-key", {});
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch (e) {
      setRotateErr((e as Error).message);
    } finally {
      setRotating(false);
    }
  }

  function copyOverlay(): void {
    if (!overlayUrl) return;
    navigator.clipboard.writeText(overlayUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  return (
    <>
      <div className="dash-page-head">
        <div>
          <h1 className="dash-page-title">Settings</h1>
          <p className="dash-page-sub">profile · living cast · overlay · account</p>
        </div>
      </div>

      {/* Living Cast (v2) */}
      <div className="dash-card elev">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Living Cast</h2>
          <span className="dash-card-sub">v2 · AI character + voice per supporter</span>
        </div>
        <div className="dash-toggle">
          <div className="dash-toggle-info">
            <div className="dash-toggle-title">Enable AI character + voice for new donors</div>
            <div className="dash-toggle-sub">
              Each new supporter gets an Imagen 4 Ultra portrait + a locked ElevenLabs voice.
            </div>
          </div>
          <button
            type="button"
            className={`dash-switch${aiEnabled ? " on" : ""}`}
            onClick={() => setAiEnabled((v) => !v)}
            aria-pressed={aiEnabled}
            aria-label="Toggle Living Cast"
          />
        </div>
        <p style={{ color: "var(--text-mute)", fontSize: 11, fontFamily: "var(--f-mono)", letterSpacing: ".06em", marginTop: 10 }}>
          {/* TODO: persist this. The toggle is UI-only — backend wiring needs a streamer.ai_enabled column + check in functions/api/donations. */}
          UI-only · backend wiring pending
        </p>
      </div>

      {/* OBS Live Strip */}
      {streamer ? (
        <div className="dash-card">
          <div className="dash-card-head">
            <h2 className="dash-card-title">OBS Live Strip</h2>
            <span className="dash-card-sub">private · paste into Browser Source</span>
          </div>
          <div className="dash-share-row" style={{ marginBottom: 10 }}>
            <code>{overlayUrl}</code>
            <button className="dash-btn dash-btn-pri" onClick={copyOverlay}>
              {copied ? "✓ Copied" : "Copy URL"}
            </button>
          </div>
          <div className="dash-share-row">
            <button className="dash-btn" onClick={rotate} disabled={rotating}>
              {rotating ? "Rotating…" : "Rotate URL"}
            </button>
            {rotateErr ? <span style={{ color: "var(--danger, #ff5252)", alignSelf: "center" }}>{rotateErr}</span> : null}
          </div>
        </div>
      ) : null}

      {/* Voice pool */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Voice pool</h2>
          <span className="dash-card-sub">{VOICE_POOL.length} ElevenLabs voices · read-only</span>
        </div>
        <p style={{ color: "var(--text-sec)", margin: "0 0 12px 0", fontSize: 13 }}>
          Each supporter is deterministically assigned one of these voices (FNV-1a hash on
          handle). Once locked, a supporter keeps that voice forever — same voice on every wall.
        </p>
        <div className="dash-voice-pool-grid">
          {VOICE_POOL.map((v, i) => (
            <div key={`${v.name}-${i}`} className="dash-voice-chip">
              <span className="dash-voice-chip-name">{v.name}</span>
              <span className="dash-voice-chip-meta">
                {v.gender === "n" ? "neutral" : v.gender === "f" ? "fem" : "masc"} · {v.vibe}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Profile */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Profile</h2>
          <span className="dash-card-sub">public · shown to donors</span>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>
            Profile URL
          </span>
          <div style={{ display: "flex", alignItems: "stretch", border: "1px solid var(--line-hot)", borderRadius: 8, overflow: "hidden", background: "var(--bg-elev)" }}>
            <code style={{ padding: "10px 12px", color: "var(--text-mute)", fontFamily: "var(--f-mono)", fontSize: 13, background: "var(--bg-card)", borderRight: "1px solid var(--line)", whiteSpace: "nowrap", display: "flex", alignItems: "center" }}>
              subswall.com/
            </code>
            <input
              type="text"
              value={handle}
              onChange={(e) => {
                const v = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24);
                setHandle(v);
                setHandleErr(null);
              }}
              maxLength={24}
              placeholder="your_handle"
              className="dash-input"
              style={{ border: 0, borderRadius: 0, background: "transparent", fontFamily: "var(--f-mono)", flex: 1 }}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
          <span style={{ color: "var(--text-mute)", fontSize: 11 }}>
            3–24 chars: a–z, 0–9, underscore. Changing this breaks any links you've already shared.
          </span>
        </label>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
          <button className="dash-btn dash-btn-pri" onClick={saveHandle} disabled={handleSaving || !handle}>
            {handleSaving ? "Saving…" : "Save handle"}
          </button>
          {handleSaved ? <span style={{ color: "var(--ok, #4caf50)" }}>Saved ✓</span> : null}
          {handleErr ? <span style={{ color: "var(--danger, #ff5252)" }}>{handleErr}</span> : null}
        </div>

        <hr style={{ border: 0, borderTop: "1px solid var(--line)", margin: "18px 0" }} />

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>
            Bio
          </span>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, 140))}
            maxLength={140}
            rows={3}
            placeholder="Streaming and chasing factions every day"
            className="dash-input"
            style={{ resize: "vertical", width: "100%" }}
          />
          <span style={{ color: "var(--text-mute)", fontSize: 11, alignSelf: "flex-end" }}>
            {bio.length}/140
          </span>
        </label>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
          <button className="dash-btn dash-btn-pri" onClick={saveBio} disabled={bioSaving}>
            {bioSaving ? "Saving…" : "Save"}
          </button>
          {bioSaved ? <span style={{ color: "var(--ok, #4caf50)" }}>Saved ✓</span> : null}
          {bioErr ? <span style={{ color: "var(--danger, #ff5252)" }}>{bioErr}</span> : null}
        </div>
      </div>

      {/* Account */}
      <div className="dash-card">
        <div className="dash-card-head">
          <h2 className="dash-card-title">Account</h2>
          <span className="dash-card-sub">identity · linked platforms</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "10px 18px", alignItems: "center", fontSize: 14 }}>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>Handle</span>
          <span><code style={{ fontFamily: "var(--f-mono)" }}>@{user.handle}</code></span>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>Email</span>
          <span>{user.email ?? <em style={{ color: "var(--text-mute)" }}>none</em>}</span>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>Platforms</span>
          <span>
            {user.twitch_linked ? <span style={{ display: "inline-block", fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", padding: "3px 7px", border: "1px solid currentColor", borderRadius: 3, marginRight: 6, textTransform: "uppercase", color: "#9146FF" }}>Twitch</span> : null}
            {user.google_linked ? <span style={{ display: "inline-block", fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", padding: "3px 7px", border: "1px solid currentColor", borderRadius: 3, marginRight: 6, textTransform: "uppercase", color: "#FF0000" }}>Google</span> : null}
            {!user.twitch_linked && !user.google_linked ? <em style={{ color: "var(--text-mute)" }}>none linked</em> : null}
          </span>
          <span style={{ fontFamily: "var(--f-mono)", fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--text-mute)" }}>Role</span>
          <span>{user.is_creator ? "Creator" : "Donor"}</span>
        </div>
        <div style={{ marginTop: 14 }}>
          <a className="dash-btn" href="/auth/logout">Log out</a>
        </div>
      </div>

      {/* Danger zone */}
      <div className="dash-card" style={{ borderColor: "var(--danger, #ff5252)" }}>
        <div className="dash-card-head">
          <h2 className="dash-card-title" style={{ color: "var(--danger, #ff5252)" }}>Danger zone</h2>
        </div>
        <p style={{ color: "var(--text-sec)", margin: "0 0 14px 0" }}>
          Delete your account and all donations. This is permanent.
        </p>
        <button className="dash-btn" disabled title="Coming soon" style={{ opacity: .5 }}>
          Delete my data (coming soon)
        </button>
      </div>
    </>
  );
}
